import { Component, OnInit, ViewChild } from '@angular/core';
import { Property } from '../../domain/property';
import { TaskService } from '../../services/task.service';
import { Task } from '../../domain/task';
import { MessageService } from 'primeng/api';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { WorkflowTasksStoreService } from '../../services/stores/workflow-tasks-store.service';
import { LoadingOverlayStoreService } from '../../services/stores/loading-overlay-store.service';
import { RefreshDataState } from '../../domain/refresh-data-state';
import { WorkflowType } from '../../domain/enums/workflow-type';
import { UserPermissions } from '../../domain/security/user-permissions';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';
import { firstValueFrom } from 'rxjs';
import { DailyTaskSummaryComponent } from './daily-task-summary/daily-task-summary.component';
import { MyUnclaimedTasksComponent } from './my-unclaimed-tasks/my-unclaimed-tasks.component';
import { MyFollowUpsComponent } from './my-follow-ups/my-follow-ups.component';
import { MyClaimedTasksComponent } from './my-claimed-tasks/my-claimed-tasks.component';
import { MyMentionsComponent } from './my-mentions/my-mentions.component';
import { TopWidgetPanelComponent } from './top-widget-panel/top-widget-panel.component';
import { AiTasksComponent } from './ai-tasks/ai-tasks.component';
import { CommonModule } from '@angular/common';
import { LoadingState } from '../../domain/loading-state';
import { DataStoreService } from '../../services/data-store.service';
import { PhobosTaskInstanceService } from '../../services/phobos-task-instance.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { WidgetStoreService } from '../../services/stores/widget-store.service';
import { WidgetType } from '../../services/util/widget-type';
import { TaskUtils } from '../../util/task-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { FollowUpStoreService } from '../../services/stores/follow-up-store.service';
import { MentionStoreService } from '../../services/stores/mention-store.service';
import { DropDownStoreService } from '../../services/stores/drop-down-store.service';
import { WidgetPanelComponent } from './widget-panel/widget-panel.component';
import { AiSummaryStoreService } from '../../services/stores/ai-summary-store.service';
import { ModelSummary } from '../../domain/Ai/model-summary';
import { DateTime } from 'luxon';
import { DatePropertyTimezonePipe } from '../../pipes/date-property-timezone.pipe';
import { ReportStoreService } from '../../services/stores/report-store.service';
import { ReportRequest } from '../../services/request/report-request';
import { DataStaffChart } from '../../domain/data-staff-chart';
import { AuthService } from '@auth0/auth0-angular';
import { AnalysisComponent } from './analysis/analysis.component';
import { AiSummary } from '../../domain/Ai/ai-summary';

type UnclaimedTaskWithAiResult = {
   task: Task;
   aiSummary?: ModelSummary | null;
};

@Component({
   selector: 'app-dashboard',
   standalone: true,
   providers: [DatePropertyTimezonePipe],
   imports: [
      CommonModule,
      TopWidgetPanelComponent,
      MyUnclaimedTasksComponent,
      DailyTaskSummaryComponent,
      MyClaimedTasksComponent,
      MyMentionsComponent,
      AiTasksComponent,
      ProgressSpinnerModule,
      MyFollowUpsComponent,
      WidgetPanelComponent,
      AnalysisComponent
   ],
   templateUrl: './dashboard.component.html',
   styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
   properties: Property[];
   unclaimedTasks: Task[];
   filteredTasks: Task[];
   unclaimedTasksWithAi: UnclaimedTaskWithAiResult[] = [];
   isLoadingUnclaimedTasks: boolean = false;
   isClaimingUnclaimedTasks: boolean = false;
   isLoading: boolean = true;
   currentProperty: Property | null = null;
   userPermissions: UserPermissions = new UserPermissions();
   outstandingTasksCount: number = 0;
   outstandingTaskList: Task[] = [];
   followUpCount: number = 0;
   mentionCount: number = 0;
   unclaimedTaskCount: number = 0;
   isDropDownDisabled: boolean = true;
   summaryDate: string | null = null;
   modelSummaries: ModelSummary[] = [];
   staffAnalysisData: DataStaffChart | null = null;
   isLoadingStaffAnalysis = false;
   private currentUserEmail: string | null = null;
   private latestAiSummary: AiSummary | null = null;

   @ViewChild('widgetPanel') widgetPanel!: WidgetPanelComponent;
   activeTab: string = 'unclaimed';

   constructor(
      private workflowTaskStore: WorkflowTasksStoreService,
      private propStore: PropertyStoreService,
      private taskService: TaskService,
      private messageService: MessageService,
      private permissionsStore: PermissionsStoreService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private dataStoreService: DataStoreService,
      private formTaskService: PhobosTaskInstanceService,
      private widgetStoreService: WidgetStoreService,
      private followUpStore: FollowUpStoreService,
      private mentionStore: MentionStoreService,
      private router: Router,
      private dropDownStore: DropDownStoreService,
      private route: ActivatedRoute,
      private aiSummaryStore: AiSummaryStoreService,
      private datePipe: DatePropertyTimezonePipe,
      private reportStore: ReportStoreService,
      private auth: AuthService
   ) {
      this.properties = [];
      this.unclaimedTasks = [];
      this.filteredTasks = [];
   }

   async ngOnInit(): Promise<void> {
      this.isLoading = true;
      this.isLoadingUnclaimedTasks = true;
      this.userPermissions = await firstValueFrom(this.permissionsStore.get());

      this.route.queryParams.subscribe((params) => {
         const tabFromURL = params['tab'];
         const savedTab = localStorage.getItem('activeTab');
         const effectiveTab = tabFromURL || savedTab || 'unclaimed';

         if (effectiveTab === 'outstanding') {
            this.goToOutstandingTasks();
         } else {
            this.setActiveTab(effectiveTab);
         }
      });

      this.auth.user$.subscribe((user) => {
         this.currentUserEmail = user?.email?.toLowerCase() ?? null;

         // If property is already selected, retry analysis now
         if (this.currentUserEmail && this.currentProperty?.id) {
            this.loadStaffAnalysis(this.currentProperty.id);
         }
      });

      this.aiSummaryStore.summary$.subscribe((summary) => {
         if (!summary) return;

         this.latestAiSummary = summary;

         // try enrichment if tasks already exist
         this.tryBuildUnclaimedTasksWithAi();
      });

      this.dropDownStore.dropDownState$.subscribe((state) => {
         this.isDropDownDisabled = state;
      });

      this.propStore.currentProperty.subscribe((property) => {
         if (!property?.id) return;

         if (property?.id) {
            this.loadStaffAnalysis(property.id);
         }

         this.currentProperty = property;

         const timezone = this.datePipe.currentPropertyTimezone;

         this.summaryDate = DateTime.now()
            .setZone(timezone)
            .minus({ days: 1 })
            .toFormat('dd/MM/yyyy');

         this.unclaimedTasks = [];
         this.filteredTasks = [];
         this.isLoadingUnclaimedTasks = true;
         this.unclaimedTasksWithAi = [];
         this.latestAiSummary = null;

         if (property) {
            this.filterTasks(this.unclaimedTasks);
            this.dropDownStore.setDropDownState(false);
         }
         if (this.currentProperty && this.unclaimedTasks.length > 0) {
            this.isLoading = false;
         } else {
            this.isLoadingUnclaimedTasks = false;
         }
      });

      this.workflowTaskStore.unclaimedTasks.subscribe((tasks) => {
         this.unclaimedTasks = tasks;
         this.filterTasks(tasks);
         this.tryBuildUnclaimedTasksWithAi();
         this.filteredTasks = [...this.filteredTasks];
         this.isLoading = false;
      });

      this.followUpStore.followUps.subscribe((followUps) => {
         this.followUpCount = followUps.length;
      });

      this.mentionStore.mentions.subscribe((mentions) => {
         this.mentionCount = mentions.length;
      });

      // Subscribe to the Outstanding Tasks count
      this.widgetStoreService.widgets.subscribe((widgets) => {
         const outstandingWidget = widgets.find(
            (widget) => widget.type === WidgetType.ClaimedTasks
         );
         if (outstandingWidget) {
            this.outstandingTasksCount = outstandingWidget.count;
         }
      });

      this.widgetStoreService.widgets.subscribe((widgets) => {
         const outstandingWidget = widgets.find(
            (widget) => widget.type === WidgetType.ClaimedTasks
         );
         if (outstandingWidget) {
            this.outstandingTasksCount = outstandingWidget.count;
            this.outstandingTaskList = outstandingWidget.data;
         }
      });
   }

   private buildAiLookup(summary: AiSummary): Map<number, ModelSummary> {
      const map = new Map<number, ModelSummary>();

      summary.summaryResponses?.forEach((day) => {
         day.modelSummaries?.forEach((model) => {
            if (model?.taskInstanceId != null) {
               map.set(model.taskInstanceId, model);
            }
         });
      });

      return map;
   }

   private tryBuildUnclaimedTasksWithAi(): void {
      if (!this.unclaimedTasks.length) return;
      if (!this.latestAiSummary) return;

      const aiLookup = this.buildAiLookup(this.latestAiSummary);

      this.unclaimedTasksWithAi = this.unclaimedTasks.map((task) => ({
         task,
         aiSummary:
            task.processId != null ? (aiLookup.get(task.processId) ?? null) : null
      }));

      // ✅ REQUIRED LOG
      console.log(
         '[Dashboard] Unclaimed Tasks enriched with AI:',
         this.unclaimedTasksWithAi
      );
   }

   private loadStaffAnalysis(propertyId: number): void {
      this.isLoadingStaffAnalysis = true;

      if (!this.currentUserEmail) {
         return;
      }

      const request = new ReportRequest();
      request.start = DateTime.now().minus({ days: 30 }).toISODate();
      request.finish = DateTime.now().toISODate();

      this.reportStore.runStaffQuery(propertyId, request).subscribe({
         next: (records) => {
            const currentUserEmail = this.currentUserEmail;
            console.log(records);
            this.staffAnalysisData = currentUserEmail
               ? (records.find((r) => r.email?.toLowerCase() === currentUserEmail) ??
                 null)
               : null;

            this.isLoadingStaffAnalysis = false;
         },
         error: () => {
            this.staffAnalysisData = null;
            this.isLoadingStaffAnalysis = false;
         }
      });
   }

   claimTasks(taskList: Task[]) {
      if (this.currentProperty) {
         this.isClaimingUnclaimedTasks = true;

         // Set loading state
         const loadingState = new LoadingState();
         loadingState.UnclaimedTasks = true;
         this.loadingOverlayStore.setLoadingState(loadingState);

         const taskIds: number[] = [];

         // Identify the type and prepare for claim
         for (const task of taskList) {
            if (task.type === WorkflowType.Form && task.taskId) {
               this.formTaskService
                  .claimAssignmentInstance(task.taskId)
                  .subscribe((amount) => {
                     console.log(`Claimed ${amount} form tasks`);
                  });
            } else {
               // Different logic for Phobos-enabled properties
               if (this.currentProperty.isPhobosEnabled) {
                  if (task.taskId && task.taskId > 0) {
                     taskIds.push(task.taskId);
                  }
               } else {
                  if (task.processId && task.processId > 0) {
                     taskIds.push(task.processId);
                  }
               }
            }
         }

         // Make the API call to mark them as claimed
         this.taskService.claimTasks(taskIds, this.currentProperty.id).subscribe(
            () => {
               // Remove from frontend list
               this.filteredTasks = this.filteredTasks.filter(
                  (task) => !taskIds.includes(task.processId ?? task.taskId)
               );

               // Remove from unclaimedTask list as well
               this.unclaimedTasks = this.unclaimedTasks.filter(
                  (task) => !taskIds.includes(task.processId ?? task.taskId)
               );

               // Reset loading state
               this.isClaimingUnclaimedTasks = false;
               const loadingState = new LoadingState();
               loadingState.UnclaimedTasks = false;
               this.loadingOverlayStore.setLoadingState(loadingState);

               // Show success message
               this.messageService.add({
                  severity: 'success',
                  summary: 'Tasks Successfully Claimed',
                  detail: `${taskList.length} tasks claimed`
               });

               this.workflowTaskStore.triggerClaimedTaskRefresh();
            },
            (error) => {
               console.log(error);

               // Show error message
               this.messageService.add({
                  severity: 'error',
                  summary: 'Unable to claim Tasks',
                  detail: `There was an error claiming ${taskList.length} tasks`
               });

               // Reset loading
               this.isClaimingUnclaimedTasks = false;
            },
            () => {
               this.isClaimingUnclaimedTasks = false;

               // Refresh the store
               const refreshDate = new RefreshDataState();
               refreshDate.ClaimedTasks = true;
               refreshDate.UnclaimedTasks = true;
               this.dataStoreService.refreshTaskData(refreshDate);
            }
         );
      }
   }

   goToOutstandingTasks(): void {
      if (this.outstandingTaskList.length > 0 && this.currentProperty) {
         // Navigate to the first task's detail page
         TaskUtils.goToTask(
            this.outstandingTaskList[0],
            this.router,
            this.currentProperty
         );
      } else {
         console.warn(
            'No Outstanding Tasks found to navigate to, or property is null.'
         );
      }
   }

   handleWidgetClick(widgetType: string) {
      if (widgetType === 'outstanding') {
         this.goToOutstandingTasks();
      } else {
         this.setActiveTab(widgetType);
      }
   }

   toggleWidgetPanel(): void {
      if (this.widgetPanel) {
         this.widgetPanel.toggleWidget();
      }
   }

   private filterTasks(tasks: Task[]): void {
      if (!this.userPermissions.forms.read) {
         this.filteredTasks = tasks.filter((f) => {
            return f.type != WorkflowType.Form;
         });
      } else {
         this.filteredTasks = tasks;
      }
   }

   private setActiveTab(tab: string): void {
      this.activeTab = tab;
      localStorage.setItem('activeTab', tab);
   }

   protected readonly DatePropertyTimezonePipe = DatePropertyTimezonePipe;
}
